import React, {useState} from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { TextField, InputAdornment, Typography } from '@mui/material';
import Stack from '@mui/material/Stack';
import { useCreateInventoryTransaction } from '../../../hooks/inventory/useCreateInventoryTransaction';
import { useUpdateInventoryTransaction } from '../../../hooks/inventory/useUpdateInventoryTransaction';
import { useSnackbar } from '../../../context/SnackbarContext';
import { useTranslation } from 'react-i18next';
import FlowRadioGroup from '../../common/controls/FlowRadioGroup';
import TransactionTypeSelect from '../controls/TransactionTypeSelect';
import AsyncAutocomplete from '../../common/controls/AsyncAutocomplete';
import { getPets } from '../../../api/petService';
import { getPetById } from '../../../api/petService';
import { getInventoryItems } from '../../../api/inventoryService';
import { getInventoryItemById } from '../../../api/inventoryService';
import { getInventorySuppliers } from '../../../api/inventoryService';
import { getInventorySupplierById } from '../../../api/inventoryService';
import { isFutureDate } from '../../../utils/isFutureDate';
import ConfirmDialog from '../../common/modals/ConfirmDialog';
import { removeZeroDecimals } from '../../../utils/removeZeroDecimals';
import DatePickerMui from '../../common/controls/DatePickerMui';
import dayjs from 'dayjs';

export default function InventoryTransactionForm({ successCallback = null, transaction = null, item = null, mode = "create", open, onClose }) {

    const { t } = useTranslation('common'); 
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

    const [itemId, setItemId] = React.useState(transaction ? transaction.item_id : item ? item.id : "");
    const [supplierId, setSupplierId] = React.useState(transaction ? transaction.supplier_id : "");
    const [disableSupplierSelect, setDisableSupplierSelect] = React.useState(false);
    const [petId, setPetId] = React.useState(transaction ? transaction.pet_id : "");
    const [typeId, setTypeId] = React.useState(transaction ? transaction.type_id : "");
    const [flow, setFlow] = React.useState(transaction ? transaction.flow : "");
    const [quantity, setQuantity] = React.useState(transaction ? transaction.quantity : "");
    const [remarks, setRemarks] = React.useState(transaction?.remarks ?? "");
    const [date, setDate] = React.useState(transaction ? transaction.date : "");

    const [uom, setUom] = React.useState("");
    const [stockDisplay, setStockDisplay] = React.useState("");
    const [currentStock, setCurrentStock] = React.useState(0);

    const { create } = useCreateInventoryTransaction(successCallback);
    const { update } = useUpdateInventoryTransaction(successCallback);

    const { showSnackbar } = useSnackbar();

    React.useEffect(() => {
        // reset field when there are changes (e.g. when opening edit)
        setItemId(transaction ? transaction.item_id : item ? item.id : "");
        setSupplierId(transaction ? transaction.supplier_id : "");
        setDisableSupplierSelect(false);
        setPetId(transaction ? transaction.pet_id : "");
        setTypeId(transaction ? transaction.type_id : "");
        setFlow(transaction ? transaction.flow : "");
        setQuantity(transaction ? transaction.quantity :"");
        setRemarks(transaction?.remarks ?? "");
        setDate(transaction ? transaction.date : "");
        setUom("");
        setStockDisplay("");
        setCurrentStock(0);
        setMuteButtonText("Mute this");
    }, [item, transaction, open]);

    const handleDateChange = (date) => {
        setDate(date);
    };

    const [muteButtonText, setMuteButtonText] = useState("Mute this");

    const handleMuteConfirm = () => {
        if (muteButtonText === "Mute this") {
            setMuteButtonText("Muted");
            localStorage.setItem("muteTransactionSaveMessage", true);
        } else {
            setMuteButtonText("Mute this");
            localStorage.setItem("muteTransactionSaveMessage", false);
        }
    };

    const handleSaveButtonClick = () => {
        !localStorage.getItem("muteTransactionSaveMessage") ? setConfirmDialogOpen(true) : handleSave();
    };

    const handleSave = async () => {

        setConfirmDialogOpen(false);

        const isValidCurrency = (value) => {
            // Matches numbers like "1", "1.23", ".9", ".50"
            return /^(?:\d+|\d*\.\d{1,2})$/.test(value);
        };

        if (!itemId) {
            showSnackbar(t('inventory.transactions.messages.item_required'), "warning");
            return;
        }
        if (!typeId) {
            showSnackbar(t('inventory.transactions.messages.type_required'), "warning");
            return;
        }
        if (!flow.trim()) {
            showSnackbar(t('inventory.transactions.messages.flow_required'), "warning");
            return;
        }
        if (!quantity || !isValidCurrency(quantity)) {
            showSnackbar(t('inventory.transactions.messages.invalid_quantity'), "warning");
            return;
        }
        if (flow === 'out' && (Number(quantity) > Number(currentStock))) {
            showSnackbar(t('inventory.transactions.messages.insufficient_stock'), "warning");
            return;
        }
        if (!date.trim()) {
            showSnackbar(t('inventory.transactions.messages.date_required'), "warning");
            return;
        }
        if (isFutureDate(date)) {
            showSnackbar(t('inventory.transactions.messages.future_date_not_allowed'), "warning");
            return;
        }

        // Build payload dynamically
        const payload = {
            item_id: itemId,
            type_id: typeId,
            flow,
            quantity: Number(quantity),
            date
        };

        if (supplierId) {
            payload.supplier_id = supplierId;
        }
        if (petId) {
            payload.pet_id = petId;
        }
        if (remarks && remarks.trim() !== "") {
            payload.remarks = remarks;
        }

        try { 
            if (mode === "create") {
                await create(payload);
                showSnackbar(t('inventory.transactions.messages.create_success'), "success");
            } else if (mode === "edit" && transaction) {
                await update(transaction.id, payload);
                showSnackbar(t('inventory.transactions.messages.update_success'), "success");
            }

            // Reset state
            setItemId("");
            setSupplierId("");
            setDisableSupplierSelect(false);
            setPetId("");
            setTypeId("");
            setFlow("");
            setQuantity("");
            setRemarks("");
            setDate("");
            setUom("");
            setStockDisplay("");
            setCurrentStock(0);
            setMuteButtonText("Mute this");
            onClose(); // close dialog
        } catch (err) {
            console.log("err.response", err.response);
            showSnackbar(t('inventory.transactions.messages.save_fail'), "error");
        }
    };

    const handleCancel = () => {
        setItemId("");
        setSupplierId("");
        setDisableSupplierSelect(false);
        setPetId("");
        setTypeId("");
        setFlow("");
        setQuantity("");
        setRemarks("");
        setDate("");
        setUom("");
        setStockDisplay("");
        setCurrentStock(0);
        setMuteButtonText("Mute this");
        onClose();
    };

    return (
        <>
        <Dialog onClose={handleCancel} open={open} disableRestoreFocus>

        <DialogTitle>{mode === "create" ? "Add Transaction" : "Edit Transaction"}</DialogTitle>

        <DialogContent dividers>
            <Stack spacing={1}>
            <DatePickerMui
                disablePast
                value={date ? dayjs(date) : dayjs()}
                label="Transaction Date"
                onChange={handleDateChange}
            />
            <TransactionTypeSelect
                label="Transaction Type"
                value={typeId}
                onChange={(e) => setTypeId(e.target.value)}
            />
            <AsyncAutocomplete
                label="Select Item"
                value={itemId}
                onChange={(id, object) => {
                    setItemId(id);
                    if (object) {
                        setUom(object.uom);
                        setStockDisplay(`stock ${removeZeroDecimals(object.current_stock)}${object.uom} | cost ${object.cost_price}/${object.uom} | price ${object.selling_price}/${object.uom}`);
                        setCurrentStock(object.current_stock);
                        if (object.supplier_id) {
                          setSupplierId(object.supplier_id);
                          setDisableSupplierSelect(true);
                        }
                    } else {
                      setSupplierId("");
                      setDisableSupplierSelect(false);
                    }
                }}
                fetchFn={getInventoryItems}
                fetchByIdFn={getInventoryItemById}
                getOptionLabel={(option) => `${option.name}`}
            />
            <AsyncAutocomplete
                label="Select Pet"
                value={petId}
                onChange={(id) => setPetId(id)}
                fetchFn={getPets}
                fetchByIdFn={getPetById}
                getOptionLabel={(option) => `${option.name} (${option.owner.last_name}, ${option.owner.first_name})`}
            />
            <AsyncAutocomplete
                disabled={disableSupplierSelect}
                label="Select Supplier"
                value={supplierId}
                onChange={(id) => setSupplierId(id)}
                fetchFn={getInventorySuppliers}
                fetchByIdFn={getInventorySupplierById}
                getOptionLabel={(option) => option.name}
            />
            <TextField
            label="Quantity"
            variant="outlined"
            type="number"
            value={quantity}
            onChange={(e) => {
                const val = e.target.value;
                // Allow empty string for controlled input
                if (val === "") {
                setQuantity("");
                return;
                }

                // Parse as float
                const num = parseFloat(val);

                // Regex: valid number with up to 2 decimals
                const valid = /^(\d+(\.\d{0,2})?|\.?\d{1,2})$/;

                if (!isNaN(num) && valid.test(val) && num > 0) {
                setQuantity(val); // keep string to preserve decimals
                }
            }}
            fullWidth
            slotProps={{
                input: {
                endAdornment: (
                    <InputAdornment position="end">{uom || ""}</InputAdornment>
                ),
                },
            }}
            />
            <Typography variant="body2" sx={{ mt: 0, fontSize: ".9rem" }}>{`${stockDisplay}`}</Typography>
            <FlowRadioGroup 
                value={flow} 
                onChange={(e) => setFlow(e.target.value)}
            />
            <TextField
                label="Remarks"
                variant="outlined"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                fullWidth
                multiline
                minRows={2}   // minimum height
                maxRows={5}  // optional: cap the expansion
            />
            </Stack>
        </DialogContent>

        <DialogActions>
            <Button onClick={handleSaveButtonClick}>
                Save
            </Button>
            <Button onClick={handleCancel}>Cancel</Button>
        </DialogActions>
        </Dialog>
        <ConfirmDialog
            leftButtonText={muteButtonText}
            onLeftButtonClick={handleMuteConfirm}
            open={confirmDialogOpen}
            title="Note On Transaction Records"
            content={t('inventory.transactions.messages.no_edit_delete')}
            confirmText="Save"
            cancelText="Cancel"
            onConfirm={handleSave}
            onCancel={() => setConfirmDialogOpen(false)}
        />
        </>
    );
}